import { renderTwig } from "@/lib/twig/twig";
import { isObject } from "@/util/objects";
import { getHash } from "@/util/hash";
import NotificationLogger from "@/lib/notifications/notifications";
import { inlineTryCatchIIFE } from "@/util/prevent-exceptions";
import {
  appendBreakdanceVersionToUrl,
  replaceVariableInDependency
} from "@/lib/canvas/assets/dom-utils";
import { PropertiesData } from "@/types/tree";
import { getDocumentHead } from "@/util/dom";

const queuedScriptResolveCallbacks: {
  [key: string]: (() => void)[];
} = {};

export function addJavaScriptsInSettingsToHead(
  canvasDocument: Document,
  settingsDataInTheCorrectShape: PropertiesData
) {
  const scripts = settingsDataInTheCorrectShape?.settings?.code?.scripts ?? [];

  scripts.forEach((script: unknown) => {
    if (isObject(script) && "code" in script) {
      addOrRerunInlineScriptInHead(canvasDocument, {
        // @ts-ignore
        code: script.code,
        origin: `addJavaScriptsInSettingsToHead`
      });
    }
  });
}

export function processAndAddInlineScriptsToHead(
  canvasDocument: Document,
  inlineScripts: string[] | undefined,
  elementProperties: unknown,
  uniqueInlineScripts = true
) {
  inlineScripts?.forEach(script => {
    const processedScript = renderTwig(
      script,
      isObject(elementProperties) ? elementProperties : {}
    );

    if (!processedScript) return;

    if (uniqueInlineScripts) {
      addUniqueInlineScriptToHead(
        canvasDocument,
        processedScript,
        "processAndAddInlineScriptsToHead"
      );
    } else {
      addOrRerunInlineScriptInHead(canvasDocument, {
        code: processedScript,
        origin: "processAndAddInlineScriptsToHead"
      });
    }
  });
}

export function addUniqueScriptToHead(
  canvasDocument: Document,
  { scriptUrl }: { scriptUrl: string }
) {
  return new Promise<void>((resolve, reject) => {
    const tagId = getHash(scriptUrl);
    /* Add script only once.
       Resolve if it's already added *and* loaded
       Note: a race condition can happen when multiple inlineScripts depend on the same
       script, but are from different elements.
       To solve this, we have a dictionary of all the 'resolve' callbacks  for each
       tagId (script) after the first one.
       When the first script loads, it'll resolve all of them and itself, which
       will run the dependent 'inlineScripts'.
     */
    const tagIdInCanvas = canvasDocument.getElementById(tagId);

    if (tagIdInCanvas?.dataset.loaded === "true") {
      resolve();
    } else if (tagIdInCanvas) {
      // Push a new callback if tagId exists, otherwise create the first
      if (queuedScriptResolveCallbacks[tagId]) {
        queuedScriptResolveCallbacks[tagId].push(resolve);
      } else {
        queuedScriptResolveCallbacks[tagId] = [resolve];
      }
    } else {
      const tag = canvasDocument.createElement("script");
      const correctUrl = replaceVariableInDependency(
        scriptUrl,
        // Needed for tests
        window.Breakdance.stores.configStore.neededData?.elementsPluginUrl,
        window.Breakdance.stores.configStore.neededData
          ?.elementsReusableDependenciesUrls
      );

      const srcUrl = appendBreakdanceVersionToUrl(
        correctUrl,
        window.Breakdance.stores.configStore.neededData?.breakdanceVersion,
        window.Breakdance.stores.configStore.neededData?.wpContentUrl
      );

      tag.id = tagId;
      tag.src = srcUrl;
      tag.async = true;

      tag.onload = () => {
        canvasDocument
          .getElementById(tagId)
          ?.setAttribute("data-loaded", "true");

        runAllQueuedResolveCallbacksForScript(tagId);

        resolve();
      };

      tag.onerror = () => {
        NotificationLogger.log.message(`Failed to load "${srcUrl}"`);
        reject();
      };

      getDocumentHead(canvasDocument).appendChild(tag);
    }
  });
}

function runAllQueuedResolveCallbacksForScript(tagId: string) {
  const keyOfCallbacksToRun = Object.keys(queuedScriptResolveCallbacks).find(
    queuedTagId => queuedTagId === tagId
  );

  if (keyOfCallbacksToRun) {
    queuedScriptResolveCallbacks[keyOfCallbacksToRun].forEach(resolveCallback =>
      resolveCallback()
    );

    // once called
    queuedScriptResolveCallbacks[keyOfCallbacksToRun] = [];
  }
}

export function addOrRerunInlineScriptInHead(
  canvasDocument: Document,
  {
    code,
    tagIdArg,
    origin
  }: {
    code: string;
    tagIdArg?: string;
    origin: string;
  }
) {
  const tagId = tagIdArg || getHash(code);
  const currentTag = canvasDocument.getElementById(tagId);

  if (currentTag) {
    currentTag.remove();
  }

  const tag = canvasDocument.createElement("script");
  tag.id = tagId;

  tag.innerHTML = inlineTryCatchIIFE(
    code,
    `An error occurred while running an inline script from ${origin}`
  );

  getDocumentHead(canvasDocument).appendChild(tag);
}

export function addUniqueInlineScriptToHead(
  canvasDocument: Document,
  code: string,
  origin: string
) {
  const tagId = getHash(code);

  if (!canvasDocument.getElementById(tagId)) {
    const tag = document.createElement("script");
    tag.id = tagId;
    tag.innerHTML = inlineTryCatchIIFE(
      code,
      `An error occurred while running an inline script from ${origin}`
    );

    getDocumentHead(canvasDocument).appendChild(tag);
  }
}
